/**
 * Utility functions for patient-related operations
 */

/**
 * Safely formats a patient DoB string (YYYY-MM-DD) for display.
 * Manually parses date components to avoid the UTC timezone off-by-one
 * bug that occurs with parseISO() + toLocaleDateString().
 */
export function formatDob(dob: string | null | undefined): string {
  if (!dob) return 'N/A';
  try {
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    return `${month}/${day}/${year}`;
  } catch {
    return dob;
  }
}

/**
 * Formats a patient DoB with age in years, e.g. "5/15/1990 (35y)".
 */
export function formatDobWithAge(dob: string | null | undefined): string {
  if (!dob) return 'N/A';
  const formatted = formatDob(dob);
  if (formatted === 'N/A') return formatted;
  try {
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return `${formatted} (${age}y)`;
  } catch {
    return formatted;
  }
}

/**
 * Formats a patient's name with optional preferred name in quotes
 * @param firstName - Patient's first name
 * @param lastName - Patient's last name
 * @param preferredName - Patient's preferred name (optional)
 * @returns Formatted name string (e.g., "John Smith" or 'John "Johnny" Smith')
 */
export const formatPatientName = (
  firstName?: string | null,
  lastName?: string | null,
  preferredName?: string | null
): string => {
  const first = firstName || '';
  const last = lastName || '';
  const preferred = preferredName || '';

  if (preferred) {
    return `${first} "${preferred}" ${last}`.trim();
  }
  return `${first} ${last}`.trim();
};

export type PatientPrimaryContactInfo = {
  mobile_phone?: string | null;
  phone?: string | null;
  telephone?: string | null;
  email?: string | null;
};

export const getPatientPrimaryContactPhone = (
  patient?: PatientPrimaryContactInfo | null,
): string => {
  if (!patient) return '';

  return patient.mobile_phone?.trim() || patient.phone?.trim() || patient.telephone?.trim() || '';
};

export const hasPatientPrimaryContactPhone = (
  patient?: PatientPrimaryContactInfo | null,
): boolean => Boolean(getPatientPrimaryContactPhone(patient));

export const hasPatientContactMethod = (
  patient?: PatientPrimaryContactInfo | null,
): boolean => Boolean(getPatientPrimaryContactPhone(patient) || patient?.email?.trim());

/**
 * Composes a medication's display detail string from its strength, dosage,
 * and frequency fields. Skips strength when the dosage value already contains
 * it (case-insensitive substring match) — guards against legacy records where
 * the dosage string was stored as e.g. "200mg Oral" with strength baked in.
 * Empty/whitespace-only fields are dropped so there are no stray separators.
 *
 * @param strength - e.g. "50mg"
 * @param dosage - e.g. "1 tablet" (or legacy "200mg Oral")
 * @param frequency - e.g. "QHS"
 * @param separator - joiner between non-empty parts (default " ")
 */
export const formatMedicationDetail = (
  strength: string | null | undefined,
  dosage: string | null | undefined,
  frequency: string | null | undefined,
  separator: string = ' '
): string => {
  const trimmedStrength = strength?.trim() ?? '';
  const trimmedDosage = dosage?.trim() ?? '';
  const trimmedFrequency = frequency?.trim() ?? '';

  const dosageContainsStrength =
    trimmedStrength.length > 0 &&
    trimmedDosage.toLowerCase().includes(trimmedStrength.toLowerCase());

  return [
    dosageContainsStrength ? '' : trimmedStrength,
    trimmedDosage,
    trimmedFrequency,
  ]
    .filter(Boolean)
    .join(separator);
};
